import { slugify } from "@repo/adapter-core";

// Ray White multi-office mapping. The national raywhite.com domain has no
// suburb-rolled-up sold-properties page (verified 2026-05-16: every URL
// shape of `raywhite.com/sold/<state>/<suburb-postcode>/` returns 404). Each
// Ray White office runs its own subdomain (dynamics.net real-estate CMS)
// and exposes a per-office sold list at
//   https://<office>/properties/sold-residential
// The HTML is SSR — the first ~50 listings are embedded inline as JSON and
// detail-page links match
//   /properties/sold-residential/<state>/<suburb>-<postcode>/<type>/<id>
// We post-filter detail URLs by the target suburb-postcode segment so a
// multi-suburb office's coverage area doesn't contaminate the run.
//
// The map below seeds the Melbourne offices we've previously scheduled
// scrapes for. Extend the map as the suburbs table grows. A suburb without
// an office mapping causes `buildSearchUrl` to throw, which surfaces in
// the discovery_tasks.error_message column.

interface OfficeMappingEntry {
  office: string;
  /** "vic", "nsw", etc. — used by the detail-URL filter. */
  state: string;
}

/**
 * Suburb-postcode (lowercase, dash-joined e.g. "brunswick-3056") →
 * office subdomain. Multiple offices can cover the same suburb in
 * Ray White's network; pick the closest geographically.
 */
const OFFICE_BY_SUBURB_POSTCODE: Record<string, OfficeMappingEntry> = {
  // Canberra (ACT)
  "barton-2600": { office: "raywhitecanberra.com.au", state: "act" },
  "kingston-2604": { office: "raywhitecanberra.com.au", state: "act" },
  "manuka-2603": { office: "raywhitecanberra.com.au", state: "act" },
  // Inner-north Melbourne
  "brunswick-3056": { office: "raywhitebrunswick.com.au", state: "vic" },
  "fitzroy-3065": { office: "raywhitebrunswick.com.au", state: "vic" },
  "carlton-3053": { office: "raywhitebrunswick.com.au", state: "vic" },
  "carlton-north-3054": { office: "raywhitebrunswick.com.au", state: "vic" },
  "north-melbourne-3051": { office: "raywhitebrunswick.com.au", state: "vic" },
  "northcote-3070": { office: "raywhitebrunswick.com.au", state: "vic" },
  "thornbury-3071": { office: "raywhitebrunswick.com.au", state: "vic" },
  // Bayside / inner-south
  "port-melbourne-3207": { office: "raywhiteportphillip.com.au", state: "vic" },
  "south-melbourne-3205": { office: "raywhiteportphillip.com.au", state: "vic" },
  "st-kilda-3182": { office: "raywhiteportphillip.com.au", state: "vic" },
  "albert-park-3206": { office: "raywhiteportphillip.com.au", state: "vic" },
  "middle-park-3206": { office: "raywhiteportphillip.com.au", state: "vic" },
  // South-east Melbourne
  "bentleigh-3204": { office: "raywhitecarnegie.com.au", state: "vic" },
  "mckinnon-3204": { office: "raywhitecarnegie.com.au", state: "vic" },
  "carnegie-3163": { office: "raywhitecarnegie.com.au", state: "vic" },
  "ormond-3204": { office: "raywhitecarnegie.com.au", state: "vic" },
  "glen-huntly-3163": { office: "raywhitecarnegie.com.au", state: "vic" },
  "elsternwick-3185": { office: "raywhitecarnegie.com.au", state: "vic" },
  // Inner-east (Stonnington corridor)
  "toorak-3142": { office: "raywhitestkilda.com.au", state: "vic" },
  "south-yarra-3141": { office: "raywhitestkilda.com.au", state: "vic" },
  "prahran-3181": { office: "raywhitestkilda.com.au", state: "vic" },
  "armadale-3143": { office: "raywhitestkilda.com.au", state: "vic" },
};

export function suburbPostcodeKey(suburb: { locality: string; postcode: string | number }): string {
  return `${slugify(suburb.locality)}-${suburb.postcode}`;
}

export function lookupRayWhiteOffice(suburb: {
  locality: string;
  postcode: string | number;
}): OfficeMappingEntry | undefined {
  return OFFICE_BY_SUBURB_POSTCODE[suburbPostcodeKey(suburb)];
}

export function listMappedSuburbs(): string[] {
  return Object.keys(OFFICE_BY_SUBURB_POSTCODE);
}
